import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcProductHotelFill16 from "@traveloka/icon-kit-web/react/IcProductHotelFill16";
import IcTransportFlightFill16 from "@traveloka/icon-kit-web/react/IcTransportFlightFill16";
import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { getXAxisTickInterval } from "../utils";
import { convert } from "../../../utils/currency";
import { formatCurrency } from "../../../utils/intl";
import BMView from "../../View/BMView";

import AnalyticsLineChart, {
  ChartTooltipProps,
  LineSeries,
} from "../AnalyticsLineChart/AnalyticsLineChart";
import { ViewType } from "../types";
import { SpendingEntry, SpendingOverviewResult } from "./types";

type Props = {
  data: SpendingOverviewResult;
  viewType: ViewType;
  interactive?: boolean;
};

function getBucketKey(entry: SpendingEntry, viewType: ViewType) {
  if (viewType === "QUARTERLY") return `${entry.quarter} ${entry.year}`;
  if (viewType === "ANNUALLY") return entry.year;
  return `${entry.month} ${entry.year}`;
}

export default function SpendingOverviewLineChart({
  data,
  viewType,
  interactive = true,
}: Props) {
  const { CorporateTransactionDataAnalytics: cr } = useMobileContentResource();

  const { FLIGHT, HOTEL } = data.spendingOverview;

  const chartData = useMemo(() => {
    const buckets = new Map<string, Record<string, number>>();
    const truncatedDates = new Map<string, string>();

    const addTransactions = (
      product: "FLIGHT" | "HOTEL",
      transactions: SpendingEntry[] = []
    ) => {
      transactions.forEach((entry) => {
        const key = getBucketKey(entry, viewType);
        const existing = buckets.get(key);
        buckets.set(key, {
          ...existing,
          [product]:
            (existing?.[product] ?? 0) + Number(entry.amount.displayAmount),
        });

        const existingDate = truncatedDates.get(key);
        if (
          !existingDate ||
          new Date(entry.truncatedDate).getTime() <
            new Date(existingDate).getTime()
        ) {
          truncatedDates.set(key, entry.truncatedDate);
        }
      });
    };

    addTransactions("FLIGHT", FLIGHT?.transactions);
    addTransactions("HOTEL", HOTEL?.transactions);

    return Array.from(buckets.entries())
      .map(([date, values]) => ({ date, ...values }))
      .sort(
        (a, b) =>
          new Date(truncatedDates.get(a.date) ?? 0).getTime() -
          new Date(truncatedDates.get(b.date) ?? 0).getTime()
      );
  }, [FLIGHT, HOTEL, viewType]);

  const series: LineSeries[] = useMemo(
    () => [
      ...(FLIGHT
        ? [
            {
              key: "FLIGHT",
              color: BMPrimitives.BMBrand.productFlights,
              label: cr.flightText,
            },
          ]
        : []),
      ...(HOTEL
        ? [
            {
              key: "HOTEL",
              color: BMPrimitives.BMBrand.productHotels,
              label: cr.hotelText,
            },
          ]
        : []),
    ],
    [cr, FLIGHT, HOTEL]
  );

  const firstEntry = FLIGHT?.transactions[0] ?? HOTEL?.transactions[0];

  const { yMin, yAxisDomain, yAxisTicks } = useMemo(() => {
    const allValues = [
      ...(FLIGHT?.transactions.map((t) => Number(t.amount.displayAmount)) ??
        []),
      ...(HOTEL?.transactions.map((t) => Number(t.amount.displayAmount)) ?? []),
    ];
    const [maxValue, minValue] =
      allValues.length > 0
        ? [Math.max(...allValues), Math.min(...allValues)]
        : [0, 0];
    const [rawMax, rawMin] = [maxValue * 1.05, minValue * 0.5];

    // guard against log10(0) = -Infinity when all values are zero or no data
    if (rawMax <= 0) {
      return {
        yMin: 0,
        yAxisDomain: [0, 1] as [number, number],
        yAxisTicks: [0, 0.2, 0.4, 0.6, 0.8, 1],
      };
    }

    const magMax = Math.pow(10, Math.floor(Math.log10(rawMax)) - 1);
    const magMin =
      rawMin > 0 ? Math.pow(10, Math.floor(Math.log10(rawMin)) - 1) : magMax;
    const yMax = Math.ceil(rawMax / magMax) * magMax;
    const yMin = rawMin > 0 ? Math.floor(rawMin / magMin) * magMin : 0;

    return {
      yMin,
      yAxisDomain: [yMin, yMax] as [number, number],
      yAxisTicks: Array.from(
        { length: 6 },
        (_, i) => yMin + ((yMax - yMin) / 5) * i
      ),
    };
  }, [FLIGHT, HOTEL]);

  const formatYAxisTick = useMemo(
    () => (v: string | number) => {
      if (v === yMin) return "";
      return new Intl.NumberFormat("id-ID").format(Number(v) / 1_000_000_000);
    },
    [yMin]
  );

  const formatXAxisTick = useCallback(
    (v: string | number) => {
      const str = String(v);
      if (viewType === "ANNUALLY") return str;

      const [period, year] = str.split(" ");
      if (viewType === "QUARTERLY") return `${period} '${year.slice(2)}`;
      return `${period.slice(0, 3)} '${year.slice(2)}`;
    },
    [viewType]
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: ChartTooltipProps) => {
      if (!interactive || !active || !payload?.length) return null;
      return (
        <SpendingChartTooltip
          label={label}
          payload={payload}
          firstEntry={firstEntry}
          viewType={viewType}
        />
      );
    },
    [firstEntry, interactive, viewType]
  );

  return (
    <AnalyticsLineChart
      data={chartData}
      series={series}
      yAxisWidth={25}
      yAxisTicks={yAxisTicks}
      yAxisDomain={yAxisDomain}
      formatYAxisTick={formatYAxisTick}
      formatXAxisTick={formatXAxisTick}
      xAxisInterval={getXAxisTickInterval(viewType, chartData.length)}
      renderTooltip={renderTooltip}
      note={cr.inBillions}
    />
  );
}

const ICON_MAP: Record<string, React.ComponentType> = {
  FLIGHT: IcTransportFlightFill16,
  HOTEL: IcProductHotelFill16,
};

type SpendingChartTooltipProps = {
  label?: string;
  payload: NonNullable<ChartTooltipProps["payload"]>;
  firstEntry:
    | { amount: { currency: string; amount: string; decimalPoints: string } }
    | undefined;
  viewType: ViewType;
};

function SpendingChartTooltip({
  label,
  payload,
  firstEntry,
  viewType,
}: SpendingChartTooltipProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const { full, range } = useMemo(() => {
    if (!label) return { full: "", range: "" };

    if (viewType === "ANNUALLY" || viewType === "QUARTERLY") {
      return { full: label, range: "" };
    }

    const [month, year] = label.split(" ");
    if (!month || !year) return { full: "", range: "" };

    const daysInMonth = new Date(
      Number(year),
      new Date(`${month} 1`).getMonth() + 1,
      0
    ).getDate();

    return { full: label, range: `(1 - ${daysInMonth} ${month.slice(0, 3)})` };
  }, [label, viewType]);

  return (
    <BMView style={styles.container} spacing="md">
      {full || range ? (
        <BMView row align="center" spacing="xs">
          <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
            {full}
          </BMText>
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {range}
          </BMText>
        </BMView>
      ) : null}
      <BMView spacing="xs">
        {payload.map((entry) => {
          const dataKey =
            typeof entry.dataKey === "string" ? entry.dataKey : "";
          const Icon = ICON_MAP[dataKey];
          const formatted = firstEntry
            ? formatCurrency(
                convert({
                  ...firstEntry.amount,
                  displayAmount: String(entry.value),
                })
              )
            : String(entry.value);

          return (
            <BMView key={entry.dataKey} row align="center" spacing="xs">
              {Icon ? <Icon /> : null}
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                {String(entry.name)}:
              </BMText>
              <BMText variant="paragraphSmallEm">{formatted}</BMText>
            </BMView>
          );
        })}
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      padding: BMPrimitives.BMSize.sm,
      borderRadius: BMPrimitives.BMRadius.md,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thin,
    },
  });
}
